import { Injectable } from '@angular/core';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { CompatibilityResultData } from '../model/compatibility-result-data';
import { ConstantsService } from './constants.service';
import { BrowserPlatformService } from './browser-platform.service';
import ashaPhones_thrive from '../../assets/android-phones-data/asha_phones_thrive.json';
import ashaPhones_afton from '../../assets/android-phones-data/asha_phones_afton.json';
import aftonLEAudioPhones from '../../assets/android-phones-data/afton_le_audio_phones.json';
import untestedPhones from '../../assets/android-phones-data/untested_phones.json'
import supportedPhones_thrive from '../../assets/android-phones-data/supported_phones.json';

declare function getRenderer(complete: (value: string) => void): void;


@Injectable({
  providedIn: 'root'
})
export class DeviceCompatibilityService {
  deviceInfo: DeviceInfo;
  maySupportTwoWayAudio: boolean = false;
  cannotUpdateAndroidOS: boolean = false;

  constructor(private deviceService: DeviceDetectorService,
              private constantsService: ConstantsService,
              private browser: BrowserPlatformService) {
    this.deviceInfo = deviceService.getDeviceInfo();
  }

  // Returns false if device is not mobile
  public checkIfMobileDevice(): boolean {
    if(this.deviceInfo.deviceType != 'mobile' && this.deviceInfo.deviceType != 'tablet') {
      return false;
    }
    else {
      return true;
    }
  }

  public getMobileDeviceBrand() {
    switch(this.deviceInfo.device) {
      case 'iPhone': {
        return 'iPhone';
      }
      case 'iPad': {
        return 'iPad'
      }
      default: {
        return 'Android'
      }
    }
  }

  public getCompatibility(phoneModel: string = '', phonePlatformVersion: string = '', userType: string = ''): CompatibilityResultData {
    this.resetCompatibilityFlags();
    switch (this.deviceInfo.device) {
      case 'iPhone': {
        return this.getiPhoneCompatibility();  // User type does not matter
      }
      case 'iPad': {
        return this.getiPadCompatibility();  // User type does not matter
      }
      default: {
        return this.getAndroidCompatibility(phoneModel, phonePlatformVersion, userType);
      }
    }
  }

  public resetCompatibilityFlags() {
    this.maySupportTwoWayAudio = false;  // Reset the maySupportTwoWayAudio flag
    this.cannotUpdateAndroidOS = false;  // Reset the cannotUpdateAndroidOS flag
  }

  public getiPhoneCompatibility(): CompatibilityResultData {
    const height = this.browser.screen.height;
    const width = this.browser.screen.width;
    const iosVersionArray = this.getIOSVersionArray();
    const iosSupported = (iosVersionArray && iosVersionArray[0] >= 16);
    let checkCPU: boolean = false;

    //console.log("iPhone width: ",width,", height: ",height,", iosVersion: ",iosVersionArray,", iosSupported: ",iosSupported);  // @TODO: Delete later

    if (iosVersionArray && iosVersionArray[0] <= 10) {  // All iPhones with iOS 10 or older are not supported (iPhone 7 and older)
      return this.constantsService.deviceNotSupported();
    }

    // Check all iPhones between 8 and 13 Pro Max  (info from https://support.apple.com/en-us/docs)

    if (height / width == 1334 / 750) {  // 8, SE 2nd Gen, SE 3rd Gen
      if (iosSupported) {
        this.maySupportTwoWayAudio = true;  // 8 does not support 2-Way
        return this.constantsService.deviceSupported();
      }
      checkCPU = true;
    }
    else if (height / width == 1920 / 1080) {  // 8 Plus
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      checkCPU = true;
    }
    else if (height / width == 2436 / 1125) {  // X, XS, 11 Pro
      if (iosSupported) {
        this.maySupportTwoWayAudio = true;  // X and XS do not support 2-Way
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2688 / 1242) {  // XS Max, 11 Pro Max
      if (iosSupported) {
        this.maySupportTwoWayAudio = true;  // XS Max does not support 2-Way
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 1792 / 828) {  // XR, 11
      if (iosSupported) {
        this.maySupportTwoWayAudio = true;  // XR does not support 2-Way
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2778 / 1284) {  // 12 Pro Max, 13 Pro Max, 14 Plus
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2532 / 1170) {  // 12, 12 Pro, 13, 13 Pro, 14, 16e, 17e
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2340 / 1080) {  // 12 Mini, 13 Mini
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }

    if (checkCPU) {  // Check CPU, iPhones 6, 6s, 6 Plus, 6s Plus, 7, 7 Plus are not supported
      let rendererResult: CompatibilityResultData | undefined;
      try {
        getRenderer((value: string) => {
          const possibleCPUs: string[] = value.split('|');
          //              A8: 6, 6 Plus             ||             A9: 6s, 6s Plus           ||              A10: 7, 7 Plus
          if (possibleCPUs.includes("Apple A8 GPU") || possibleCPUs.includes("Apple A9 GPU") || possibleCPUs.includes("Apple A10 GPU")) {
            rendererResult = this.constantsService.deviceNotSupported();
          }
          else {  // 8, SE 2nd Gen, SE 3rd Gen, 8 Plus
            rendererResult = this.constantsService.deviceOSUpdateRequired();
          }
        })
      } catch {
        // Fall back to the conservative OS-update result if renderer detection is unavailable.
      }
      // 8, SE 2nd Gen, SE 3rd Gen, 8 Plus
      return rendererResult ?? this.constantsService.deviceOSUpdateRequired();
    }

    if (iosSupported) {  // iPhones 14 and newer are pre-installed with iOS 16 and are 2-Way capable
      // 14 Pro, 14 Pro Max, 15, 15 Pro, 15 pro Max, 15 Plus, 16, 16 Plus, 16 Pro, 16 Pro Max, 17, 17 Pro, 17 Pro Max, Air
      return this.constantsService.deviceSupportedTwoWayAudioCapable();
    }

    return this.constantsService.deviceNotSupported();
  }

  public getiPadCompatibility(): CompatibilityResultData {
    const height = this.browser.screen.height;
    const width = this.browser.screen.width;
    const iosVersionArray = this.getIOSVersionArray();
    const iosSupported = (iosVersionArray && iosVersionArray[0] >= 16);
    let checkCPU: boolean = false;

    //console.log("iPad width: ", width, ", height: ", height, ", iosVersion: ", iosVersionArray, ", iosSupported: ", iosSupported);  // @TODO: Delete later

    if (iosVersionArray && iosVersionArray[0] <= 8) {  // All iPads with iOS 8 or older are not supported (iPad Mini 3 and older)
      return this.constantsService.deviceNotSupported();
    }

    // Check all iPads between iPad Pro 1st (12.9-inch) and  iPad Air 11-in (M4) (info from https://support.apple.com/en-us/docs)

    if (height / width == 2732 / 2048) {  // 12.9in Pro and 13in Air models with this aspect ratio
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2388 / 1668) {  // 11in Pro models with this aspect ratio
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2224 / 1668) {  // Pro (10.5in), Air 3rd gen
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2048 / 1536) {  // Pro (7.7in), 5th gen, 6th gen, Mini 5th gen
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      checkCPU = true;
    }
    else if (height / width == 2160 / 1620) {  // 7th gen, 8th gen, 9th gen
      if (iosSupported) {
        return this.constantsService.deviceSupported();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2266 / 1488) {  // Mini 6th gen, Mini A17 pro
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2360 / 1640) {  // Air 4th gen, Air 5th gen, Air 11in M2, Air 11in M3, Air 11in M4, 10th gen, A16
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2420 / 1668) {  // Pro 11in M4, Pro 11in M5
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    else if (height / width == 2752 / 2064) {  // Pro 13in M4, Pro 13in M5
      if (iosSupported) {
        return this.constantsService.deviceSupportedTwoWayAudioCapable();
      }
      return this.constantsService.deviceOSUpdateRequired();
    }

    if (checkCPU) {  // Check CPU, iPads 3rd gen, 4th gen, Air 1, Air 2, Mini 2, and Mini 3 are not supported
      let rendererResult: CompatibilityResultData | undefined;
      try {
        getRenderer((value: string) => {
          const possibleCPUs: string[] = value.split('|');
          //               A5X: 3rd gen              ||              A6X: 4th gen              ||        A7: Air 1, Mini 2, Mini 3       ||              A8: Air 2
          if (possibleCPUs.includes("Apple A5X GPU") || possibleCPUs.includes("Apple A6X GPU") || possibleCPUs.includes("Apple A7 GPU")  || possibleCPUs.includes("Apple A8 GPU")) {
            rendererResult = this.constantsService.deviceNotSupported();
          }
          else {  // Pro (7.7in), 5th gen, 6th gen, Mini 5th gen
            rendererResult = this.constantsService.deviceOSUpdateRequired();
          }
        })
      } catch {
        // Fall back to the conservative OS-update result if renderer detection is unavailable.
      }
      // Pro (7.7in), 5th gen, 6th gen, Mini 5th gen
      return rendererResult ?? this.constantsService.deviceOSUpdateRequired();
    }

    if (iosSupported) {  // iPads Pro 6th Gen and newer are pre-installed with iOS 16 and are 2-Way capable
      return this.constantsService.deviceSupportedTwoWayAudioCapable();
    }

    return this.constantsService.deviceNotSupported();
  }

  private getIOSVersionArray(): number[] | null {
    const iosVersion: RegExpMatchArray | null = this.browser.navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (iosVersion) {
      const iosVersionArray: number[] = [parseInt(iosVersion[1]), parseInt(iosVersion[2] || '0'), parseInt(iosVersion[3] || '0')];
      //console.log("iosVersion:", iosVersionArray);
      return iosVersionArray;
    }
    return null;
  }


  public async getAndroidCompatibilityFromUserHints(isThrive: boolean = false) {
    const data = await this.browser.navigator.userAgentData?.getHighEntropyValues(['model', "platformVersion"])
    //console.log("Data", data)
    const model = data?.model;

    const androidVersion = data?.platformVersion;

    if(model && androidVersion) {
      const isStreamingSupportedOSVersion = this.checkIfAndroidVersionIsStreamingCompatible(androidVersion, isThrive);
      const isSupportedOSVersion = this.checkIfAndroidVersionIsCompatible(androidVersion, isThrive)

      if (this.checkIfAndroidModelIsUntested(model)) {  // If device has not undergone compatibility testing
        return this.constantsService.deviceNotFullyTested();
      }

      // Check if Afton / Engage and is LE phone first
      if (!isThrive && this.checkIfAndroidModelIsLEPhone(model)) {
        if (isSupportedOSVersion) {
            this.maySupportTwoWayAudio = true;  // Show "To learn more about streaming capabilites for your specific device" text
            return this.constantsService.deviceSupported();  // Bi-Directional streaming using LE audio
        }
        return this.constantsService.deviceOSUpdateRequired();
      }
      // ASHA Phones
      if (this.checkIfAndroidModelIsInAshaPhones(model, isThrive)) {
        if (isStreamingSupportedOSVersion) {
          return this.constantsService.deviceSupported();
        }
        else if (isSupportedOSVersion) {
          return this.constantsService.deviceSupportedStreamingNotAvailable();
        }
        else {
          return this.constantsService.deviceOSUpdateRequired();
        }
      }
      // if Thrive / Engage
      if (isThrive) {
        if (this.checkIfAndroidModelIsInSupportedPhones(model)) {  // Supported Phone for Thrive
          if (isSupportedOSVersion) {
            return this.constantsService.deviceSupportedStreamingNotAvailable();
          }
          else {
            return this.constantsService.deviceOSUpdateRequired();
          }
        }
      }
      // if Afton 
      if (!isThrive) {
        if (this.checkIfAndroidModelIsInAshaPhones(model, !isThrive)) {  // if Afton, but phone is on Thrive ASHA list
          this.cannotUpdateAndroidOS = true;  // Device cannot update to Android 14
          return this.constantsService.deviceSupportedButCannotUpdateOS();
        }
      }
      if (isSupportedOSVersion) {  // OS is supported but device is not on list
        return this.constantsService.deviceNotFullyTested();
      }
    }
    return this.constantsService.deviceNotSupported();
  }

  public getAndroidCompatibility(androidModel: string, platformVersion: string, userType: string): CompatibilityResultData {
    const isThrive = (userType === 'thrive');
    const isStreamingSupportedOSVersion = this.checkIfAndroidVersionIsStreamingCompatible(platformVersion, isThrive);
    const isSupportedOSVersion = this.checkIfAndroidVersionIsCompatible(platformVersion, isThrive);

    if (this.checkIfAndroidModelIsUntested(androidModel)) {  // If device has not undergone compatibility testing
      return this.constantsService.deviceNotFullyTested();
    }

    // Check if Afton and is LE phone first
    if (!isThrive && this.checkIfAndroidModelIsLEPhone(androidModel)) {
      if (isSupportedOSVersion) {
          this.maySupportTwoWayAudio = true;  // Show "To learn more about streaming capabilites for your specific device" text
          return this.constantsService.deviceSupported();  // Bi-Directional streaming using LE audio
      }
      return this.constantsService.deviceOSUpdateRequired();
    }
    // ASHA Phones
    if (this.checkIfAndroidModelIsInAshaPhones(androidModel, isThrive)) {
      if (isStreamingSupportedOSVersion) {
        return this.constantsService.deviceSupported();
      }
      else if (isSupportedOSVersion) {
        return this.constantsService.deviceSupportedStreamingNotAvailable();
      }
      else {
        return this.constantsService.deviceOSUpdateRequired();
      }
    }
    // if Thrive / Engage
    if (isThrive) {
      if (this.checkIfAndroidModelIsInSupportedPhones(androidModel)) {  // Supported Phone for Thrive
        if (isSupportedOSVersion) {
          return this.constantsService.deviceSupportedStreamingNotAvailable();
        }
        else {
          return this.constantsService.deviceOSUpdateRequired();
        }
      }
    }
    // if Afton
    if (!isThrive) {
      if (this.checkIfAndroidModelIsInAshaPhones(androidModel, !isThrive)) {  // if Afton/Engage, but phone is on Thrive ASHA list
        this.cannotUpdateAndroidOS = true;  // Device cannot update to Android 14
        return this.constantsService.deviceSupportedButCannotUpdateOS();
      }
    }
    if (isSupportedOSVersion) {  // OS is supported but device is not on list
      return this.constantsService.deviceNotFullyTested();
    }
    // Device not supported
    return this.constantsService.deviceNotSupported();
  }


  //private getAndroidVersion(userAgent: string) {
  //  const androidVersionMatchArray: RegExpMatchArray | null = userAgent.match(/Android\s([0-9\.]*)/i);
  //  let androidVersion = undefined;
  //  if(androidVersionMatchArray) {
  //    return androidVersionMatchArray[1];
  //  }
  //  return androidVersion;
  //}

  private checkIfAndroidVersionIsStreamingCompatible(androidVersion: string, isThrive: boolean) {
    if (isThrive) {
      return parseInt(androidVersion) >= 10 ? true : false;  // Engage
    }
    return parseInt(androidVersion) >= 14 ? true : false;  // Afton
  }

  private checkIfAndroidVersionIsCompatible(androidVersion: string, isThrive: boolean) {
    if (isThrive) {
      return parseInt(androidVersion) >= 7 ? true : false;  // Thrive
    }
    return parseInt(androidVersion) >= 14 ? true : false;  // Afton
  }

  private checkIfAndroidModelIsInAshaPhones(userAgent: string, isThrive: boolean): boolean {
    let ashaPhonesMap;
    if (isThrive) {
      ashaPhonesMap = new Map(Object.entries(ashaPhones_thrive));  // Thrive / Engage
    } else {
      ashaPhonesMap = new Map(Object.entries(ashaPhones_afton));  // Afton
    }
    for (const value of ashaPhonesMap.values()) {
      const ashaPhonesInnerMap = new Map(Object.entries(value));
      for(const value of ashaPhonesInnerMap.values()) {
        for(const model of value) {
          const regStr = RegExp('(' + model + ')');
          if (userAgent.match(regStr) != null) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private checkIfAndroidModelIsInSupportedPhones(userAgent: string): boolean {  // Supported phones for Thrive / Engage
    const supportedPhonesMap = new Map(Object.entries(supportedPhones_thrive));
    for (const value of supportedPhonesMap.values()) {
      const supportedPhonesInnerMap = new Map(Object.entries(value));
      for(const value of supportedPhonesInnerMap.values()) {
        const regStr = RegExp('(' + value[0] + ')');
        if (userAgent.match(regStr) != null) {
          return true;
        }
      }
    }
    return false;
  }

  private checkIfAndroidModelIsLEPhone(userAgent: string): boolean {  // Check if Phone has LE Audio for Bi-Directional Streaming
    const leAudioPhonesMap = new Map(Object.entries(aftonLEAudioPhones));
    for (const value of leAudioPhonesMap.values()) {
      const leAudioPhonesInnerMap = new Map(Object.entries(value));
      for (const value of leAudioPhonesInnerMap.values()) {
        const regStr = RegExp('(' + value[0] + ')');
        if (userAgent.match(regStr) != null) {
          return true;
        }
      }
    }
    return false;
  }

  private checkIfAndroidModelIsUntested(userAgent: string): boolean {  // Check if Phone has not undergone compatibility testing
    const untestedPhonesMap = new Map(Object.entries(untestedPhones));
    for (const value of untestedPhonesMap.values()) {
      const untestedPhonesInnerMap = new Map(Object.entries(value));
      for (const value of untestedPhonesInnerMap.values()) {
        const regStr = RegExp('(' + value[0] + ')');
        if (userAgent.match(regStr) != null) {
          return true;
        }
      }
    }
    return false;
  }
}
